import React, { ChangeEvent, useState } from 'react';
import { initCaseFormChange, setFormChanged } from 'app/shared/reducers/case-form-change';
import { FormikProvider, useFormik } from 'formik';
import { useDispatch } from 'react-redux';
import { Button } from 'edozvola-frontend-lib/dist/ui/components';
import { Alert, Col, Modal, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';
import { Input, Label } from 'edozvola-frontend-lib/dist/ui';
import { useParams } from 'react-router-dom';
import {
  CaseZoningPlanComplianceModel,
  zoningPlanComplianceInitialData,
} from 'app/edozvola/cases/case-zoning-plan-compliance/model/case-zoning-plan-compliance.model';
import {
  fetchIspuZoningPlanQuery,
  fetchZoningPlanComplianceQuery,
  saveZoningPlanComplianceQuery,
} from 'app/edozvola/cases/case-zoning-plan-compliance/service/case-zoning-plan-compliance.queries';
import { fetchLocationDataSourceQuery } from 'app/edozvola/cases/case-location/service/case-location.queries';
import CaseZoningPlanTable from 'app/edozvola/cases/case-zoning-plan-compliance/case-zoning-plan-table';
import CaseZoningPlanForm from 'app/edozvola/cases/case-zoning-plan-compliance/case-zoning-plan-form';
import { fetchUrbanPlanOblgiationQuery } from 'app/edozvola/codebooks/codebooks.queries';
import FormikValueObserver from 'app/shared/formik-observer/formik-observer';
import { ToggleWithLabels } from 'edozvola-frontend-lib/dist/ui/components/toggle-with-labels';
import { getCaseContext } from 'app/shared/util/context-util';
import { Loader } from "app/shared/loader/loader";

const ZoningPlanCompliance = () => {
  const { id: uuid } = useParams<{ id: string }>();

  const dispatch = useDispatch();
  const { sidebarItemPair } = getCaseContext();

  const [idCaseZoningPlan, setIdCaseZoningPlan] = useState<string | null>(null);
  const [readOnly, setReadOnly] = useState(false);
  const [showFetchPlansConfirmModal, setShowFetchPlansConfirmModal] = useState(false);

  const { data: urbanPlanObligationOptions, isLoading: loadingUrbanPlanObligationOptions } = fetchUrbanPlanOblgiationQuery();
  const { data: zoningPlanComplianceData, isLoading: zoningPlanComplianceDataLoading } = fetchZoningPlanComplianceQuery(uuid);
  const { data: locationDataSource } = fetchLocationDataSourceQuery(uuid);
  const { mutate: fetchIspuZoningPlanMutation, isPending: isFetchingIspuZoningPlan } = fetchIspuZoningPlanQuery(uuid);

  const formik = useFormik({
    initialValues: (zoningPlanComplianceData as CaseZoningPlanComplianceModel) || zoningPlanComplianceInitialData,
    onSubmit: values => saveZoningPlanComplianceMutation(values),
    enableReinitialize: true,
    validateOnMount: true,
  });

  const { mutate: saveZoningPlanComplianceMutation, isPending: savingZoningPlanCompliance } = saveZoningPlanComplianceQuery(
    uuid,
    formik.resetForm,
  );

  const openNewCaseZoningPlan = () => {
    setIdCaseZoningPlan('');
    setReadOnly(false);
  };

  const handleFetchZoningPlanClick = () => {
    if (zoningPlanComplianceData?.hasExistingZoningPlans) {
      setShowFetchPlansConfirmModal(true);
    } else {
      fetchIspuZoningPlanMutation();
    }
  };

  const confirmFetchZoningPlan = () => {
    setShowFetchPlansConfirmModal(false);
    fetchIspuZoningPlanMutation();
  };

  const onFinalActIssuedToggled = (e: ChangeEvent<HTMLInputElement>) => {
    formik.setValues({
      ...formik.values,
      compatibilityExplanation: '',
      finalActIssued: e.target.checked,
    });
  };

  return (
    <>
      {isFetchingIspuZoningPlan && <Loader />}
      <FormikProvider value={formik}>
        <FormikValueObserver
          onChange={dirty => {
            if (dirty.dirty) {
              dispatch(setFormChanged(true));
            } else {
              dispatch(initCaseFormChange());
            }
          }}
        />
        <Row>
          <Col md={6}>
            <Label labelText={'Završni akt se izdaje temeljem prostornog plana'} />
            <ToggleWithLabels
              trueLabel={'DA'}
              falseLabel={'NE'}
              checked={formik.values.finalActIssued}
              onChange={onFinalActIssuedToggled}
              disabled={sidebarItemPair?.readOnly}
            />
          </Col>
        </Row>
        <Row>
          <Col xs={6}>
            {formik.dirty && !zoningPlanComplianceData.finalActIssued && formik.values.finalActIssued && (
              <Alert color="warning">Potrebno je prvo spremiti izmjenu na kartici da biste mogli dodati prostorne planove!</Alert>
            )}
          </Col>
        </Row>
        <hr />
        <Row>
          <Col md={6}>
            <div className={'form-header mb-4'}>Prostorni planovi vezani uz predmet</div>
          </Col>
          <Col md={6}>
            {!sidebarItemPair?.readOnly && (
              <>
                <div className={'d-flex justify-content-end pb-4 gap-2'}>
                  <div>
                    <Button
                      label={'Dohvati važeće planove'}
                      color={'primary'}
                      size={'lg'}
                      disabled={
                        !locationDataSource?.locationDatasource ||
                        isFetchingIspuZoningPlan ||
                        (formik.dirty && !zoningPlanComplianceData.finalActIssued) ||
                        !formik.values.finalActIssued
                      }
                      onClick={handleFetchZoningPlanClick}
                    />
                  </div>
                  <div>
                    <Button
                      label={'Dodaj prostorni plan'}
                      color={'info'}
                      prependIcon={'plus'}
                      size={'lg'}
                      onClick={openNewCaseZoningPlan}
                      disabled={(formik.dirty && !zoningPlanComplianceData.finalActIssued) || !formik.values.finalActIssued}
                    />
                  </div>
                </div>
              </>
            )}
          </Col>
        </Row>
        <>
          <CaseZoningPlanTable finalActIssued={!formik.dirty && zoningPlanComplianceData?.finalActIssued && formik.values?.finalActIssued} />
          {idCaseZoningPlan !== null && (
            <CaseZoningPlanForm idCaseZoningPlan={idCaseZoningPlan} setIdCaseZoningPlan={setIdCaseZoningPlan} readOnly={readOnly} />
          )}
          <hr />
          <div className={'mt-4 d-flex flex-column gap-3'}>
            <Row>
              <Col md={6}>
                <Label labelText={'Obrazloženje usklađenosti zahvata s prostornim planom'} />
                <Input
                  type={'textarea'}
                  maxLength={500}
                  value={formik.values.compatibilityExplanation}
                  disabled={sidebarItemPair?.readOnly || !formik.values.finalActIssued}
                  onChange={event => formik.setFieldValue('compatibilityExplanation', event.target.value)}
                />
              </Col>
            </Row>
          </div>
          {!sidebarItemPair?.readOnly && (
            <div className={'case-save-button'}>
              <Button
                label={'Spremi'}
                color={'info'}
                size={'lg'}
                isLoading={savingZoningPlanCompliance}
                disabled={!formik.dirty || !formik.isValid}
                onClick={() => formik.handleSubmit()}
              />
            </div>
          )}
        </>
      </FormikProvider>

      <Modal scrollable isOpen={showFetchPlansConfirmModal} size={'lg'} toggle={() => setShowFetchPlansConfirmModal(false)} centered>
        <ModalHeader>
          <span>Dohvat važećih planova</span>
        </ModalHeader>
        <ModalBody>
          <div>Dohvatom podataka o planovima obrisat će se svi prethodno dodani prostorni planovi! Želite li nastaviti s dohvatom?</div>
        </ModalBody>
        <ModalFooter>
          <Button size={'lg'} label={'Odustani'} outline color={'secondary'} onClick={() => setShowFetchPlansConfirmModal(false)} />
          <Button size={'lg'} label={'Dohvati'} color={'info'} onClick={confirmFetchZoningPlan} />
        </ModalFooter>
      </Modal>
    </>
  );
};

export default ZoningPlanCompliance;
